import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  colors,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { useAlert } from "react-alert";
import ReactModal from "react-modal";

import { Alert } from "@material-ui/lab";

import usePublishTrackerBlocker from "../mutations/usePublishTrackerBlocker";
import useWaitAtLeast from "../../hooks/useWaitAtLeast";
import { CheckmarkIcon } from "../../components/icons/themes/windows10";

const useStyles = makeStyles((theme) => ({
  cardHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottomColor: theme.palette.grey[200],
    borderBottomStyle: "solid",
    borderBottomWidth: 1,
  },
  modalActions: {
    alignContent: "center",
    justifyContent: "right",
    marginBottom: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  cancelBtn: {
    background: theme.palette.background.default,
    marginRight: theme.spacing(2),
  },
}));

type EnableTrackerBlockerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

function EnableTrackerBlockerModal({
  isOpen,
  onClose,
  onConfirm,
}: EnableTrackerBlockerModalProps) {
  const classes = useStyles();
  const customStyles = {
    content: {
      zIndex: 10050,
      minHeight: `20vh`,
      maxHeight: 700,
      maxWidth: "30em",
      top: "auto",
      bottom: "auto",
      right: "auto",
      left: "auto",
      position: "static" as "static",
      display: "flex",
      padding: 0,
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, .5)",
      zIndex: 10000,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Warning: PHI may be shared from your site"
    >
      <Card>
        <CardHeader
          className={classes.cardHeader}
          title="Warning: PHI may be shared from your site"
        />
        <CardContent>
          <Typography component="p" variant="body1">
            <strong>Are you sure you want to enable tracker blocking?</strong>{" "}
            This action will enable the tracker blocker on your site.
          </Typography>

          <Alert severity="warning" style={{ marginTop: "1em" }}>
            <Typography component="p" variant="body1">
              In the event that essential trackers your site depends upon to
              function are inadvertently blocked, portions of your site may not
              function as expected.
            </Typography>
          </Alert>
        </CardContent>
        <CardActions>
          <Grid container className={classes.modalActions}>
            <Grid item>
              <Button
                className={classes.cancelBtn}
                color="inherit"
                variant="contained"
                onClick={onClose}
                size="small"
                title="Cancel"
              >
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                style={{ backgroundColor: colors.green[700], color: "white" }}
                onClick={onConfirm}
                size="small"
                title="Enable"
              >
                Enable Tracker Blocker
              </Button>
            </Grid>
          </Grid>
        </CardActions>
      </Card>
    </ReactModal>
  );
}

export type EnableTrackerBlockerButtonProps = {
  className?: string;
  fullWidth?: boolean;
  size?: "small" | "medium" | "large";
};

function EnableTrackerBlockerButton({
  className = "",
  fullWidth = true,
  size = "small",
}: EnableTrackerBlockerButtonProps) {
  const alert = useAlert();
  const [loading, setLoading] = React.useState(false);
  const [isModalOpen, setModalOpen] = useState(false);

  const onEnableTrackerBlocker = useWaitAtLeast(
    500,
    () => {
      alert.success(
        "Tracker Blocker enabled. Please allow up to five minutes for the changes to propagate to your site."
      );
      setLoading(false);
    },
    () => {
      alert.error(
        "Failed to enable tracker blocker. Please try again later or contact support for assistance."
      );
      setLoading(false);
    }
  );

  // TODO: Publishing the tracker blocker's configuration may have the same
  // effect as enabling it, but it might also be worthwhile to model this
  // action explicitly in the API. We should verify that this is a reasonable
  // stand-in for enabling the tracker blocker in the short term, and in the
  // mid-term we should make this action explicit.
  const enableTrackerBlocker = usePublishTrackerBlocker(
    onEnableTrackerBlocker.onSucceeded,
    onEnableTrackerBlocker.onFailed
  );

  const onConfirm = () => {
    if (loading) {
      return;
    }

    onEnableTrackerBlocker.onStarted();
    setModalOpen(false);
    setLoading(true);

    enableTrackerBlocker.mutate();
  };

  const buttonStyle = loading
    ? {}
    : { backgroundColor: colors.green[700], color: "white" };

  return (
    <>
      <EnableTrackerBlockerModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={onConfirm}
      />
      <Button
        className={className}
        fullWidth={fullWidth}
        style={buttonStyle}
        disabled={loading}
        size={size}
        title="Enable"
        variant="contained"
        onClick={() => setModalOpen(true)}
      >
        <CheckmarkIcon />
        &nbsp;Enable
      </Button>
    </>
  );
}

export default EnableTrackerBlockerButton;
